import type { ChapterEnum, WorkOrderChangePriceData, WorkOrderChangeSeasonData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';

import type { ColDef } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useMemo } from 'react';

import {
    WorkOrderChangeIsEditableCheck,
    WorkOrderChangeSeasonValueGetter,
    WorkOrderChangeSeasonValueRenderer,
    WorkOrderChangeSeasonVolumeEditRenderer,
} from '../CellRenderers';
import { SEASON_COST_COLUMNS_WIDTH, SEASON_VOLUME_COLUMNS_WIDTH } from '../constants';
import {
    uerWorkOrderChangeSeasonPriceCompletedVolumeFilterGql,
    uerWorkOrderChangeSeasonPricePlanVolumeFilterGql,
    uerWorkOrderChangeSeasonPriceRemainderVolumeFilterGql,
} from '../WorkOrderChangeQueries';

interface IUseWorkOrderChangeSeasonsColumns {
    workOrderChangeId: number;
    isActionsDisabled: boolean;
    handleUpdateVolume: (v: any) => void;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    workOrderObjectId: number | undefined;
    workOrderChangeObjectId: number | undefined;
    chapter: ChapterEnum;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: { data: any } }) => void;
}

export const useWorkOrderChangeSeasonsColumns = (props: IUseWorkOrderChangeSeasonsColumns) => {
    const {
        workOrderChangeId,
        workOrderChangeObjectId,
        workOrderObjectId,
        isActionsDisabled,
        chapter,
        handleUpdateVolume,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        changeFilterHandler,
    } = props;

    const getVariablesChangeVolume = useCallback(
        (data: WorkOrderChangePriceData, workOrderChangeSeasonId: number, value: number) => ({
            // @ts-ignore
            ...R.pickAll(['workOrderPriceId', 'workOrderChangePriceId'], data),
            workOrderChangeSeasonId,
            workOrderChangeId,
            volume: value,
        }),
        [workOrderChangeId],
    );

    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderObjectId,
            workOrderChangeObjectId,
            chapter,
        }),
        [chapter, workOrderChangeId, workOrderChangeObjectId, workOrderObjectId],
    );

    return useMemo(
        () =>
            workOrderChangeSeasons.reduce(
                (acc: { columns: ColDef[]; isOdd: boolean }, item: WorkOrderChangeSeasonData) => {
                    const cellClass = acc.isOdd ? 'oddSeason' : 'evenSeason';

                    const season = [
                        {
                            headerName: `Сезон ${item.year}, план`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    field: `planVolume_${item.year}`,
                                    width: SEASON_VOLUME_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonId: item.id,
                                        seasonYear: item.year,
                                        dataKey: 'planVolume',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonVolumeEditRenderer,
                                    cellRendererParams: {
                                        seasonId: item.id,
                                        handleUpdateValue: handleUpdateVolume,
                                        getVariablesChangeVolume,
                                        checkIsEditable: WorkOrderChangeIsEditableCheck(isActionsDisabled),
                                        workOrderChangeUerRules,
                                    },
                                    cellClass,
                                    // TODO: пока не включаем.
                                    // filter: AgGridCustomFilterAdapter,
                                    filterParams: {
                                        withSearch: false,
                                        hideTableHeader: true,
                                        variables: { ...filterVariables, seasonId: item.id },
                                        dataAccessor: 'uerWorkOrderChangeSeasonPricePlanVolumeFilter',
                                        query: uerWorkOrderChangeSeasonPricePlanVolumeFilterGql,
                                        childFieldAccessor: 'value',
                                        whereVariables: ['value'],
                                        columns: [
                                            {
                                                headerName: 'Объем',
                                                field: 'value',
                                                checkboxSelection: true,
                                                lockVisible: true,
                                                flex: 1,
                                            },
                                        ],
                                        changeFilterHandler,
                                    },
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'planCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({ decimal: costRounding }),
                                    cellClass,
                                },
                            ],
                        },
                        {
                            headerName: `Сезон ${item.year}, выполнено`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    field: `completedVolume_${item.year}`,
                                    width: SEASON_VOLUME_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'completedVolume',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: 3,
                                        withDeviation: true,
                                    }),
                                    cellClass,
                                    // filter: AgGridCustomFilterAdapter,
                                    filterParams: {
                                        withSearch: false,
                                        hideTableHeader: true,
                                        variables: { ...filterVariables, seasonId: item.id },
                                        dataAccessor: 'uerWorkOrderChangeSeasonPriceCompletedVolumeFilter',
                                        query: uerWorkOrderChangeSeasonPriceCompletedVolumeFilterGql,
                                        childFieldAccessor: 'value',
                                        whereVariables: ['value'],
                                        columns: [
                                            {
                                                headerName: 'Объем',
                                                field: 'value',
                                                checkboxSelection: true,
                                                lockVisible: true,
                                                flex: 1,
                                            },
                                        ],
                                        changeFilterHandler,
                                    },
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'completedCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({ decimal: costRounding }),
                                    cellClass,
                                },
                            ],
                        },
                        {
                            headerName: `Сезон ${item.year}, остаток`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    field: `remainderVolume_${item.year}`,
                                    width: SEASON_VOLUME_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'remainderVolume',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({ decimal: 3 }),
                                    cellClass,
                                    // filter: AgGridCustomFilterAdapter,
                                    filterParams: {
                                        withSearch: false,
                                        hideTableHeader: true,
                                        variables: { ...filterVariables, seasonId: item.id },
                                        dataAccessor: 'uerWorkOrderChangeSeasonPriceRemainderVolumeFilter',
                                        query: uerWorkOrderChangeSeasonPriceRemainderVolumeFilterGql,
                                        childFieldAccessor: 'value',
                                        whereVariables: ['value'],
                                        columns: [
                                            {
                                                headerName: 'Объем',
                                                field: 'value',
                                                checkboxSelection: true,
                                                lockVisible: true,
                                                flex: 1,
                                            },
                                        ],
                                        changeFilterHandler,
                                    },
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'remainderCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({ decimal: costRounding }),
                                    cellClass,
                                },
                            ],
                        },
                    ];

                    acc.columns.push(...season);
                    acc.isOdd = !acc.isOdd;

                    return acc;
                },
                {
                    columns: [],
                    isOdd: true,
                },
            ).columns,
        [
            workOrderChangeSeasons,
            handleUpdateVolume,
            getVariablesChangeVolume,
            isActionsDisabled,
            workOrderChangeUerRules,
            filterVariables,
            changeFilterHandler,
            costRounding,
        ],
    );
};
